import { AfterViewInit, Component, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import { CalendarOptions, EventClickArg, DateSelectArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Api } from '../../services/api';
import { CalendarService } from '../../services/calendar.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-calendar',
  imports: [FullCalendarModule, CommonModule, FormsModule],
  templateUrl: './calendar.html',
  styleUrl: './calendar.css'
})

export class Calendar implements AfterViewInit, OnInit{
  @ViewChild('calendar') calendarComponent!: FullCalendarComponent;

  private calendarReady: boolean = false;
  public eventsLoaded: boolean = false;
  public events: any[] = [];
  public api = inject(Api);
  public calendarService = inject(CalendarService);
  public outlook_mail: string = '';
  public outlook_connected: boolean = false;

  constructor(
    private router: Router
  ) {

  }

  ngOnInit(): void {
    this.getOutlookAccount();
  }

  ngAfterViewInit(): void {
    this.calendarReady = true;
  }

 async getOutlookAccount(){
    this.api.getOutlookInfo().subscribe({
      next: (data: any) => {
        this.outlook_mail = data.outlook_account_email;
        this.outlook_connected = data.outlook_connected;
        if(this.outlook_mail && this.outlook_connected){
          this.loadEvents();
        }
      },
      error: (error) => {
        console.error('Error fetching Outlook account:', error);
      },
    });
  }

  loadEvents() {
    this.api.getOutlookEvents().subscribe({
      next: (data: any) => {
        console.log('Eventos originales de Outlook:', data);
        
        // Transformar los eventos usando el servicio
        const transformedEvents = this.calendarService.transformOutlookEvents(data);
        console.log('Eventos transformados para FullCalendar:', transformedEvents);
        
        this.events = transformedEvents;
        this.eventsLoaded = true;
        
        // Actualizar el calendario con los nuevos eventos
        this.calendarOptions.update(options => ({
          ...options,
          events: transformedEvents
        }));
      },
      error: (error: any) => {
        console.error('Error fetching events:', error);
        this.eventsLoaded = false;
      }
    });
  }

  cuentas: any[] = [];
  cuentasFiltradas: any[] = [];
  isLoading: boolean = true;
  errorMessage: string = '';
  showRecentDropdown: boolean = false;
  selectedItem: string = 'todas';
  openedMenuId: string | null = null;
  searchTerm: string = '';
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  activeFilters: any = null;
  currentView: string = 'timeGridWeek';

  calendarOptions = signal<CalendarOptions>({
    plugins: [
      dayGridPlugin,
      timeGridPlugin,
      listPlugin,
      interactionPlugin
    ],
    initialView: 'timeGridWeek',
    locale: esLocale, // ¡Español!
    
    // Configuración del header
    headerToolbar: false,
    
    // Textos en español personalizados
    buttonText: {
      today: 'Hoy',
      month: 'Mes',
      week: 'Semana',
      day: 'Día',
      list: 'Lista'
    },
    
    // Formato de hora en español
    slotLabelFormat: {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false // Formato 24 horas
    },
    
    
    // Configuraciones adicionales
    editable: true,
    selectable: true,
    selectMirror: true,
    dayMaxEvents: true,
    weekends: true,
    nowIndicator: true, // Línea de hora actual
    
    // Altura del calendario
    height: 'auto',
    
    // Eventos de interacción
    select: this.handleDateSelect.bind(this),
    eventClick: this.handleEventClick.bind(this),
    eventsSet: this.handleEvents.bind(this),
    
    // Mostrar números de semana (opcional)
    weekNumbers: false,
    
    // Formato de fecha
    dayHeaderFormat: { weekday: 'short', day: 'numeric' },
    eventContent: function(arg) {
      // Validar que start y end existan
      if (!arg.event.start || !arg.event.end) {
        return {
          html: `<div style="padding: 2px 4px; overflow: hidden;">
                   <div style="font-weight: 600; font-size: 11px; line-height: 1.2; white-space: normal;">
                     ${arg.event.title}
                   </div>
                 </div>`
        };
      }
      
      const duration = (arg.event.end.getTime() - arg.event.start.getTime()) / (1000 * 60);
      
      if (duration < 45) {
        return {
          html: `<div style="padding: 2px 4px; font-size: 10px; font-weight: 600; line-height: 1.2;">
                   ${arg.event.title.substring(0, 20)}${arg.event.title.length > 20 ? '...' : ''}
                 </div>`
        };
      } else {
        return {
          html: `<div style="padding: 4px; font-size: 11px;">
                   <div style="font-weight: 600; margin-bottom: 2px;">${arg.event.title}</div>
                 </div>`
        };
      }
    }
  });

  handleDateSelect(selectInfo: DateSelectArg) {
    const title = prompt('📝 Ingresa el título del nuevo evento:');
    const calendarApi = selectInfo.view.calendar;

    calendarApi.unselect(); // Limpiar la selección

    if (title) {
      calendarApi.addEvent({
        title,
        start: selectInfo.startStr,
        end: selectInfo.endStr,
        allDay: selectInfo.allDay,
        backgroundColor: '#ff6b35',
        borderColor: '#ff6b35'
      });
      
      console.log('✅ Evento creado:', title);
    }
  }

  handleEventClick(clickInfo: EventClickArg) {
    const confirmMessage = `🗑️ ¿Deseas eliminar el evento "${clickInfo.event.title}"?`;
    
    if (confirm(confirmMessage)) {
      clickInfo.event.remove();
      console.log('✅ Evento eliminado:', clickInfo.event.title);
    }
  }

  handleEvents(events: any[]) {
    console.log('📅 Total de eventos:', events.length);
  }

  addNewEvent() {
    const title = prompt('📝 Ingresa el título del nuevo evento:');
    if (title) {
      const calendarApi = document.querySelector('full-calendar');
      alert('💡 Selecciona una fecha en el calendario para crear el evento');
    }
  }

  toggleRecentDropdown(): void {
    this.showRecentDropdown = !this.showRecentDropdown;
  }

  getDropdownText(): string {
    switch (this.selectedItem) {
      case 'dia': return 'Ver por dia';
      case 'semana': return 'Ver por semana';
      case 'mes': return 'Ver por mes';
      default: return 'Ver por semana';
    }
  }

  toggleActionsMenu(id: string): void {
    this.openedMenuId = this.openedMenuId === id ? null : id;
    this.showRecentDropdown = false; 
  }

  changeView(view: string): void {
    this.currentView = view;
    
    // Espera a que el calendario esté listo
    setTimeout(() => {
      const calendarApi = this.calendarComponent?.getApi();
      if (calendarApi) {
        calendarApi.changeView(view);
      }
    }, 0);
  }

  selectItem(key: string): void {
    this.selectedItem = key;
    this.showRecentDropdown = false; 
    this.changeView(key === 'dia' ? 'timeGridDay' : key === 'semana' ? 'timeGridWeek' : 'dayGridMonth');
  }

  connectOutlook() {
    const tenantId = "7eafb56d-40c8-4934-842a-c9207647bbe0";
    const clientId = "6b59dcd9-2f94-41f7-adbd-6a4050cf015c";
    const redirectUri = "http://localhost:3000/api/auth/callback"; // backend
    const scopes = [
      "openid",
      "profile",
      "offline_access",
      "User.Read",
      "Mail.Send",
      "Calendars.Read"
    ];

    const jwtLocal = localStorage.getItem("crmtoken");

    const authUrl =
      `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize` +
      `?client_id=${clientId}` +
      `&response_type=code` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_mode=query` +
      `&scope=${encodeURIComponent(scopes.join(" "))}` +
      `&state=${encodeURIComponent(jwtLocal ?? '')}`;

    window.location.href = authUrl;
  } 

}


